/**
 * SmartImprove — Client Inputs Workspace (Phase 7 refactor, Feb 2026).
 *
 * Replaces the legacy inline-form clutter (Add Issue + Add KPI + Add
 * Observation forms shown inline in the page body) with a consultant
 * operational transformation workspace:
 *
 *   1. EngagementOverviewExpanded — compact executive strip with industry,
 *      strategic objective, business model, geographic distribution,
 *      priority, warehouse + finding counts, KPI-linked findings count.
 *   2. WarehousesSitesPanel       — enhanced site cards with role badge,
 *      country, SAP/EWM landscape, automation maturity, notes preview,
 *      and per-site finding count.
 *   3. OperationalFindingsSection — workspace table with search / filter /
 *      sort and a single reusable modal for Create + Edit Finding.
 *
 * The modal captures Core Finding, Operational Impact, Warehouses,
 * Operational Analysis, KPI Impact, and Internal Metadata sections in one
 * place. Priority and Impact are surfaced as separate dimensions (Severity
 * is intentionally removed from the consultant UX; backend keeps severity
 * in lockstep with priority for downstream engines).
 *
 * Backend: existing `/engagements/{id}/issues` POST + `/issues/{id}` PUT.
 * The Phase 7 backend pass widens the IssueCreate / IssueUpdate models so
 * sub_process, impact_area, frequency_volume, observation_notes,
 * existing_workaround, applicable_site_ids, and KPI fields all persist.
 */
import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Copy,
  Search,
  Filter as FilterIcon,
  X,
  Warehouse,
  Briefcase,
  Target,
  Globe,
  Factory,
  TrendingUp,
  CircleDot,
  Trash2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  addIssue,
  updateIssue,
  addSite,
  updateSite,
  deleteSite,
} from "../../lib/smartimproveApi";
import { compareFindings } from "./findingOrder";

// ---------------------------------------------------------------------------
// Static catalogues — mirror the public intake taxonomy so the modal works
// without an extra network round-trip. The same labels resolve against the
// backend `_validate_area` helper.
// ---------------------------------------------------------------------------
const BUSINESS_PROCESSES = [
  {
    key: "organization_operations",
    label: "Organization & Operations",
    sub: ["Governance", "Operating Model", "Site Mgmt"],
  },
  {
    key: "inbound",
    label: "Inbound Processes",
    sub: [
      "ASN Processing",
      "Receiving",
      "HU Receiving",
      "Putaway",
      "Cross-Docking",
      "Inbound Quality Inspection",
    ],
  },
  {
    key: "outbound",
    label: "Outbound Processes",
    sub: [
      "Wave Management",
      "Picking",
      "Packing",
      "Loading",
      "Shipping",
      "Secondary Verification",
    ],
  },
  {
    key: "internal_warehouse",
    label: "Internal Warehouse Operations",
    sub: [
      "Replenishment",
      "Inventory Accuracy",
      "Cycle Counting",
      "Slotting",
      "Bin Management",
      "Internal Movements",
      "Task Interleaving",
    ],
  },
  {
    key: "automation",
    label: "Automation & Warehouse Technology",
    sub: [
      "RF Mobility",
      "SAP RFUI",
      "Pallet Builder",
      "Voice Picking",
      "Device Performance",
      "Warehouse Automation",
    ],
  },
  {
    key: "integration_interfaces",
    label: "Integration & Interfaces",
    sub: [
      "MES Integration",
      "ERP / EWM Integration",
      "IDoc Monitoring",
      "Queue Monitoring",
      "MFS Integration",
    ],
  },
  {
    key: "reporting",
    label: "Reporting, KPIs & Visibility",
    sub: [
      "Supervisor Dashboards",
      "Operational KPIs",
      "Exception Monitoring",
      "Labor Visibility",
      "Real-Time Analytics",
    ],
  },
  {
    key: "yard_transport",
    label: "Yard & Transportation",
    sub: ["Yard Management", "Dock Scheduling", "Gate In / Gate Out"],
  },
  {
    key: "labor_workforce",
    label: "Labor & Workforce Operations",
    sub: ["Scheduling", "Productivity", "Training", "Engagement"],
  },
  {
    key: "quality_compliance",
    label: "Quality & Compliance",
    sub: ["Audit Trails", "GxP", "Returns Quality", "Safety"],
  },
  {
    key: "future_transformation",
    label: "Future Transformation & Innovation",
    sub: ["Roadmap", "AI / ML", "Sustainability"],
  },
];

const PROCESS_LABEL = Object.fromEntries(
  BUSINESS_PROCESSES.map((p) => [p.key, p.label])
);
const PROCESS_SUBS = Object.fromEntries(
  BUSINESS_PROCESSES.map((p) => [p.key, p.sub])
);

// ---------------------------------------------------------------------------
// useProcessCatalog — loads the admin-managed Business Processes +
// Sub-processes from `/api/smartimprove/operational-areas` and
// `/api/smartimprove/sub-processes` so Business Processes added via
// Platform → Reference Models show up immediately in the consultant Edit
// Finding modal + filters + table label. Falls back to the hardcoded
// `BUSINESS_PROCESSES` defaults if the API is unreachable (offline /
// pre-seed environments).
// ---------------------------------------------------------------------------
function useProcessCatalog() {
  const [processes, setProcesses] = useState(BUSINESS_PROCESSES);
  const [subsByProcess, setSubsByProcess] = useState(PROCESS_SUBS);
  const [labelByKey, setLabelByKey] = useState(PROCESS_LABEL);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      // Lazy import so module-level constants stay self-contained.
      import("../../lib/smartimproveApi").then((m) =>
        Promise.all([m.listOperationalAreas(), m.listSubProcesses()])
      ),
    ])
      .then(([[areas, subs]]) => {
        if (cancelled) return;
        const cleanAreas = (areas || [])
          .map((a) => ({ key: a.key, label: a.label || a.key }))
          .filter((a) => a.key);
        // Group sub-processes by process_key, falling back to the
        // hardcoded `sub` list so a process with zero admin-managed
        // sub-processes still shows the legacy defaults rather than an
        // empty dropdown.
        const subMap = {};
        (subs || []).forEach((s) => {
          if (!s.process_key) return;
          if (!subMap[s.process_key]) subMap[s.process_key] = [];
          subMap[s.process_key].push(s.label || s.key);
        });
        const merged = cleanAreas.map((a) => ({
          key: a.key,
          label: a.label,
          sub: subMap[a.key] && subMap[a.key].length
            ? subMap[a.key]
            : PROCESS_SUBS[a.key] || [],
        }));
        if (merged.length) {
          setProcesses(merged);
          setSubsByProcess(
            Object.fromEntries(merged.map((p) => [p.key, p.sub]))
          );
          setLabelByKey(
            Object.fromEntries(merged.map((p) => [p.key, p.label]))
          );
        }
      })
      .catch(() => {
        // Silent fallback — keeps the dropdowns usable even if the
        // admin endpoints are temporarily unavailable.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { processes, subsByProcess, labelByKey };
}

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const IMPACT_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const IMPACT_AREA_OPTIONS = [
  "Efficiency",
  "Productivity",
  "Quality & Compliance",
  "Safety",
  "Inventory Accuracy",
  "Cost Reduction",
  "Service Level",
  "Operational Visibility",
  "Standardization",
];

// Feb 2026 simplification — 4-state finding lifecycle.
// Older statuses (open / in_analysis / validated / dismissed / reviewed /
// recommendation_ready / archived) are kept readable but no longer offered
// in the dropdown. Legacy rows automatically display as the closest active
// status via `displayStatus()` below.
const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "accepted", label: "Accepted" },
  { value: "grouped", label: "Grouped" },
  { value: "achieved", label: "Achieved" },
];
const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS.map((s) => [s.value, s.label])
);

// Map any value (legacy or active) → the active 4-state value for display.
function normalizeStatus(value) {
  if (!value) return "draft";
  if (["draft", "accepted", "grouped", "achieved"].includes(value)) return value;
  // Legacy → active mapping.
  if (value === "open" || value === "in_analysis") return "draft";
  if (value === "reviewed" || value === "validated") return "accepted";
  if (value === "recommendation_ready") return "grouped";
  if (value === "dismissed" || value === "archived") return "draft";
  return "draft";
}

function displayStatus(value) {
  return STATUS_LABEL[normalizeStatus(value)] || "Draft";
}

// SAP/Fiori-style badge tokens for the 4 active statuses.
const STATUS_CHIP = {
  draft: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  accepted: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  grouped: "bg-[#CCFBF1] text-[#0F766E] border-[#99F6E4]",
  achieved: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
};

const PRIORITY_CHIP = {
  low: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  medium: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  high: "bg-[#FED7AA] text-[#9A3412] border-[#FDBA74]",
  critical: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

const WAREHOUSE_TYPE_LABEL = {
  cdc: "Central Distribution Center",
  rdc: "Regional Distribution Center",
  fc: "Fulfillment Center",
  plant: "Plant Warehouse",
  "3pl": "3PL Site",
  other: "Other",
};

// ---------------------------------------------------------------------------
// Engagement overview strip
// ---------------------------------------------------------------------------
// UI-only helper: converts an engagement_type slug like "post_go_live" into a
// sentence-case label "Post Go Live". Never mutates stored DB values.
const ENGAGEMENT_TYPE_LABELS = {
  remote: "Remote",
  remote_review: "Remote Review",
  workshop: "Workshop",
  hybrid: "Hybrid",
  post_go_live: "Post Go Live",
  optimization: "Optimization",
};
function formatEngagementType(raw) {
  if (!raw) return "";
  if (ENGAGEMENT_TYPE_LABELS[raw]) return ENGAGEMENT_TYPE_LABELS[raw];
  return String(raw)
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-sm px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B7280]">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        <span>{label}</span>
      </div>
      <div className="mt-1 text-sm font-semibold text-[#111827] truncate">
        {value || <span className="text-[#94A3B8] font-normal">—</span>}
      </div>
    </div>
  );
}

export function EngagementOverviewExpanded({ engagement, sites, findings }) {
  const counts = useMemo(() => {
    const f = findings || [];
    return {
      total: f.length,
      kpiLinked: f.filter((x) => (x.kpi_metric_label || "").trim()).length,
      sites: (sites || []).length,
    };
  }, [findings, sites]);

  const eng = engagement || {};
  return (
    <section
      className="space-y-2"
      data-testid="ci-engagement-overview"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        <StatTile icon={Factory} label="Industry" value={eng.industry} />
        <StatTile
          icon={Target}
          label="Strategic Objective"
          value={eng.strategic_objective}
        />
        <StatTile
          icon={Briefcase}
          label="Business Model"
          value={eng.business_model}
        />
        <StatTile
          icon={Globe}
          label="Geography"
          value={eng.geographic_distribution}
        />
        <StatTile
          icon={TrendingUp}
          label="Engagement Type"
          value={formatEngagementType(eng.engagement_type)}
        />
        <StatTile
          label="Status"
          value={(eng.status || "").replace(/_/g, " ")}
        />
        <StatTile icon={Warehouse} label="Warehouses" value={counts.sites} />
        <StatTile label="Findings" value={counts.total} />
        <StatTile label="KPI-Linked Findings" value={counts.kpiLinked} />
      </div>
      {eng.notes ? (
        <div className="bg-white border border-[#E5E7EB] rounded-sm px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-[#6B7280] mb-1">
            Engagement Notes
          </div>
          <p className="text-xs text-[#374151] whitespace-pre-wrap">
            {eng.notes}
          </p>
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Warehouses / Sites panel — enhanced cards with full CRUD inline
// ---------------------------------------------------------------------------
const EMPTY_WAREHOUSE = {
  id: null,
  warehouse_name: "",
  location_city: "",
  country: "",
  warehouse_type: "cdc",
  sap_landscape: "",
  ewm_version: "",
  site_size: "",
  automation_maturity: "",
  workforce_count: "",
  shifts_per_day: "",
  annual_inbound_deliveries: "",
  annual_outbound_orders: "",
  use_labor_cost_benchmark: false,
  labor_cost_per_hour_usd: "",
  inventory_value_usd: "",
  notes: "",
};

const SITE_SIZE_OPTIONS = [
  "Small (<10k m²)",
  "Mid (10–50k m²)",
  "Large (50–100k m²)",
  "Mega (>100k m²)",
];
const AUTOMATION_OPTIONS = ["Manual", "Low", "Medium", "High", "Lights-Out Vision"];
const WAREHOUSE_TYPE_OPTIONS = [
  { value: "cdc", label: "Central Distribution Center" },
  { value: "rdc", label: "Regional Distribution Center" },
  { value: "fc", label: "Fulfillment Center" },
  { value: "plant", label: "Plant Warehouse" },
  { value: "3pl", label: "3PL Site" },
  { value: "other", label: "Other" },
];

// Warehouse Operational Baseline — shared helpers (June 2026). USD-only.
// Capture/display only; NOT wired to any ROI engine.
const obHas = (v) => v !== null && v !== undefined && v !== "";
const obCompact = (n) => {
  const v = Number(n);
  if (!isFinite(v)) return String(n ?? "");
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${(v / 1e9).toFixed(v % 1e9 === 0 ? 0 : 1)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 1)}M`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(v % 1e3 === 0 ? 0 : 1)}K`;
  return String(v);
};
const obLaborLabel = (s) => {
  if (s?.use_labor_cost_benchmark) return "PrimeS4 benchmark";
  if (obHas(s?.labor_cost_per_hour_usd))
    return `USD ${Number(s.labor_cost_per_hour_usd).toLocaleString()}/hr`;
  return "Not provided";
};
const obInvLabel = (s) => {
  if (obHas(s?.inventory_value_usd)) return `USD ${obCompact(s.inventory_value_usd)}`;
  return "Not provided";
};

// City is often not captured separately by the intake. We try, in order:
// (1) explicit `location_city` from the DB, (2) heuristic from the first
// word of warehouse_name if it doesn't already start with a generic word
// like "Central"/"Plant"/"Global"/etc. Fallback: country only.
const GENERIC_FIRST_WORDS = new Set([
  "central", "regional", "global", "plant", "main", "primary",
  "fulfillment", "distribution", "warehouse", "site", "the",
]);
function deriveLocation(site) {
  const city = (site?.location_city || "").trim();
  if (city) {
    return site?.country ? `${city}, ${site.country}` : city;
  }
  const name = (site?.warehouse_name || "").trim();
  const first = name.split(/\s+/)[0] || "";
  if (first && !GENERIC_FIRST_WORDS.has(first.toLowerCase())) {
    return site?.country ? `${first}, ${site.country}` : first;
  }
  return site?.country || "";
}

function WarehouseDialog({ open, onOpenChange, value, onChange, onSave, saving, isFinalized }) {
  if (!value) return null;
  const isEdit = !!value.id;
  const set = (p) => onChange({ ...value, ...p });
  const valid =
    !!(value.warehouse_name || "").trim() &&
    (isEdit || (value.workforce_count !== null && value.workforce_count !== undefined && String(value.workforce_count).trim() !== ""));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="ci-warehouse-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Warehouse / Site" : "Add Warehouse / Site"}
          </DialogTitle>
          <DialogDescription>
            Capture the operational landscape for this warehouse so findings
            can reference it accurately.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-1 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Field label="Warehouse name" required>
              <Input
                value={value.warehouse_name}
                onChange={(e) => set({ warehouse_name: e.target.value })}
                placeholder="e.g. Dublin Distribution Center"
                className="rounded-sm text-xs"
                data-testid="ci-warehouse-name"
              />
            </Field>
          </div>
          <Field label="City">
            <Input
              value={value.location_city}
              onChange={(e) => set({ location_city: e.target.value })}
              placeholder="e.g. Dublin"
              className="rounded-sm text-xs"
            />
          </Field>
          <Field label="Country / region">
            <Input
              value={value.country}
              onChange={(e) => set({ country: e.target.value })}
              placeholder="e.g. Ireland"
              className="rounded-sm text-xs"
            />
          </Field>
          <Field label="Warehouse type">
            <Select
              value={value.warehouse_type || ""}
              onValueChange={(v) => set({ warehouse_type: v })}
            >
              <SelectTrigger className="rounded-sm text-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {WAREHOUSE_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Site size">
            <Select
              value={value.site_size || ""}
              onValueChange={(v) => set({ site_size: v })}
            >
              <SelectTrigger className="rounded-sm text-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {SITE_SIZE_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="SAP landscape">
            <Input
              value={value.sap_landscape}
              onChange={(e) => set({ sap_landscape: e.target.value })}
              placeholder="e.g. SAP S/4HANA"
              className="rounded-sm text-xs"
            />
          </Field>
          <Field label="WMS / EWM landscape">
            <Input
              value={value.ewm_version}
              onChange={(e) => set({ ewm_version: e.target.value })}
              placeholder="e.g. Embedded EWM with RFUI"
              className="rounded-sm text-xs"
            />
          </Field>
          <Field label="Automation maturity">
            <Select
              value={value.automation_maturity || ""}
              onValueChange={(v) => set({ automation_maturity: v })}
            >
              <SelectTrigger className="rounded-sm text-xs">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {AUTOMATION_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="md:col-span-2 border-t border-[#E5E7EB] mt-1 pt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#025979]">
              Operational Baseline
            </p>
            <p className="text-[10px] text-[#94A3B8] mb-2.5">
              Used to estimate improvement value. Approximate values are fine.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Warehouse workforce" required={!isEdit} helper="Approx. operational employees">
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={value.workforce_count ?? ""}
                  onChange={(e) => set({ workforce_count: e.target.value })}
                  placeholder="e.g. 35"
                  className="rounded-sm text-xs"
                  data-testid="ci-warehouse-workforce"
                />
              </Field>
              <Field label="Operating shifts / day" helper="Optional · typically 1–3">
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={value.shifts_per_day ?? ""}
                  onChange={(e) => set({ shifts_per_day: e.target.value })}
                  placeholder="e.g. 2"
                  className="rounded-sm text-xs"
                  data-testid="ci-warehouse-shifts"
                />
              </Field>
              <Field label="Annual inbound deliveries" helper="Optional · approximate">
                <Input
                  type="number"
                  min="0"
                  value={value.annual_inbound_deliveries ?? ""}
                  onChange={(e) => set({ annual_inbound_deliveries: e.target.value })}
                  placeholder="e.g. 100,000"
                  className="rounded-sm text-xs"
                  data-testid="ci-warehouse-inbound"
                />
              </Field>
              <Field label="Annual outbound orders" helper="Optional · approximate">
                <Input
                  type="number"
                  min="0"
                  value={value.annual_outbound_orders ?? ""}
                  onChange={(e) => set({ annual_outbound_orders: e.target.value })}
                  placeholder="e.g. 250,000"
                  className="rounded-sm text-xs"
                  data-testid="ci-warehouse-outbound"
                />
              </Field>
              <Field label="Average loaded labor cost / hour (USD)" helper="Optional">
                <Input
                  type="number"
                  min="0"
                  disabled={!!value.use_labor_cost_benchmark}
                  value={value.use_labor_cost_benchmark ? "" : (value.labor_cost_per_hour_usd ?? "")}
                  onChange={(e) => set({ labor_cost_per_hour_usd: e.target.value })}
                  placeholder={value.use_labor_cost_benchmark ? "PrimeS4 benchmark" : "e.g. 28"}
                  className="rounded-sm text-xs"
                  data-testid="ci-warehouse-labor-cost"
                />
                <label className="flex items-center gap-1.5 mt-1.5 text-[10px] text-[#374151] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!value.use_labor_cost_benchmark}
                    onChange={(e) =>
                      set({
                        use_labor_cost_benchmark: e.target.checked,
                        labor_cost_per_hour_usd: e.target.checked ? "" : (value.labor_cost_per_hour_usd ?? ""),
                      })
                    }
                    data-testid="ci-warehouse-labor-benchmark"
                  />
                  Use PrimeS4 benchmark
                </label>
              </Field>
              <Field label="Approx. inventory value (USD)" helper="Optional">
                <Input
                  type="number"
                  min="0"
                  value={value.inventory_value_usd ?? ""}
                  onChange={(e) => set({ inventory_value_usd: e.target.value })}
                  placeholder="e.g. 10,000,000"
                  className="rounded-sm text-xs"
                  data-testid="ci-warehouse-inventory-value"
                />
              </Field>
            </div>
          </div>
          <div className="md:col-span-2">
            <Field label="Operational notes">
              <Textarea
                rows={3}
                value={value.notes}
                onChange={(e) => set({ notes: e.target.value })}
                className="rounded-sm text-xs"
                placeholder="What is this site used for? Key processes, scope, products handled."
              />
            </Field>
          </div>
        </div>
        <DialogFooter className="pt-3 border-t border-[#E5E7EB]">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !valid || isFinalized}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="ci-warehouse-save"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Warehouse"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function WarehousesSitesPanel({
  engagementId,
  sites,
  findings,
  onChange,
  isFinalized,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const findingCountFor = (siteId) =>
    (findings || []).filter((f) =>
      (f.applicable_site_ids || []).includes(siteId)
    ).length;

  const openCreate = () => {
    setForm({ ...EMPTY_WAREHOUSE });
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setForm({ ...EMPTY_WAREHOUSE, ...s });
    setDialogOpen(true);
  };
  const remove = async (s) => {
    if (isFinalized) {
      toast.error("Engagement is finalized — cannot remove warehouses.");
      return;
    }
    if (
      !window.confirm(
        `Remove "${s.warehouse_name}"? Findings linked to it will keep the link, but the site card will disappear.`
      )
    )
      return;
    try {
      await deleteSite(engagementId, s.id);
      toast.success("Warehouse removed");
      await onChange();
    } catch {
      toast.error("Failed to remove warehouse");
    }
  };
  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        warehouse_name: form.warehouse_name,
        location_city: form.location_city || "",
        country: form.country || "",
        warehouse_type: form.warehouse_type || "cdc",
        sap_landscape: form.sap_landscape || "",
        ewm_version: form.ewm_version || "",
        site_size: form.site_size || "",
        automation_maturity: form.automation_maturity || "",
        workforce_count: form.workforce_count,
        shifts_per_day: form.shifts_per_day,
        annual_inbound_deliveries: form.annual_inbound_deliveries,
        annual_outbound_orders: form.annual_outbound_orders,
        use_labor_cost_benchmark: !!form.use_labor_cost_benchmark,
        labor_cost_per_hour_usd: form.labor_cost_per_hour_usd,
        inventory_value_usd: form.inventory_value_usd,
        notes: form.notes || "",
      };
      if (form.id) {
        await updateSite(engagementId, form.id, payload);
        toast.success("Warehouse updated");
      } else {
        await addSite(engagementId, payload);
        toast.success("Warehouse added");
      }
      setDialogOpen(false);
      await onChange();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save warehouse");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="bg-white border border-[#E5E7EB] rounded-sm"
      data-testid="ci-warehouses-panel"
    >
      <header className="px-5 py-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Warehouse className="w-4 h-4 text-[#025979]" />
          <h2 className="text-sm font-semibold text-[#111827]">
            Warehouses / Sites
          </h2>
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]">
            {(sites || []).length}
          </span>
        </div>
        <Button
          onClick={openCreate}
          disabled={isFinalized}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs h-7 px-2.5"
          data-testid="ci-warehouse-add"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Warehouse / Site
        </Button>
      </header>
      <div className="p-4">
        {(sites || []).length === 0 ? (
          <div className="text-xs text-[#6B7280] italic">
            No warehouses captured yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 items-stretch">
            {(sites || []).map((s) => {
              const fc = findingCountFor(s.id);
              const location = deriveLocation(s);
              return (
                <div
                  key={s.id}
                  className="border border-[#E5E7EB] rounded-sm p-3 bg-[#F8FAFC] hover:bg-white transition flex flex-col"
                  data-testid={`ci-warehouse-card-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#111827] break-words">
                        {s.warehouse_name}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {s.warehouse_type ? (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]">
                            {WAREHOUSE_TYPE_LABEL[s.warehouse_type] || s.warehouse_type}
                          </span>
                        ) : null}
                        {location ? (
                          <span className="text-[10px] text-[#6B7280]">{location}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-sm bg-white border border-[#E5E7EB] text-[#374151]"
                        title="Findings applicable to this site"
                      >
                        {fc} {fc === 1 ? "finding" : "findings"}
                      </span>
                      <button
                        type="button"
                        onClick={() => openEdit(s)}
                        disabled={isFinalized}
                        className="p-1 text-[#025979] hover:bg-[#F0F7FA] rounded-sm disabled:opacity-50"
                        title="Edit warehouse"
                        data-testid={`ci-warehouse-edit-${s.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(s)}
                        disabled={isFinalized}
                        className="p-1 text-[#B91C1C] hover:bg-[#FEF2F2] rounded-sm disabled:opacity-50"
                        title="Remove warehouse"
                        data-testid={`ci-warehouse-remove-${s.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1.5 text-[11px]">
                    {s.sap_landscape ? (
                      <div className="col-span-2">
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          SAP
                        </dt>
                        <dd className="text-[#374151] break-words">
                          {s.sap_landscape}
                        </dd>
                      </div>
                    ) : null}
                    {s.ewm_version ? (
                      <div className="col-span-2">
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          WMS / EWM
                        </dt>
                        <dd className="text-[#374151] break-words">
                          {s.ewm_version}
                        </dd>
                      </div>
                    ) : null}
                    {s.site_size ? (
                      <div>
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          Size
                        </dt>
                        <dd className="text-[#374151]">{s.site_size}</dd>
                      </div>
                    ) : null}
                    {s.automation_maturity ? (
                      <div>
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          Automation
                        </dt>
                        <dd className="text-[#374151]">
                          {s.automation_maturity}
                        </dd>
                      </div>
                    ) : null}
                    {obHas(s.workforce_count) ? (
                      <div>
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          Workforce
                        </dt>
                        <dd className="text-[#374151]">{s.workforce_count}</dd>
                      </div>
                    ) : null}
                    {obHas(s.shifts_per_day) ? (
                      <div>
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          Shifts
                        </dt>
                        <dd className="text-[#374151]">{s.shifts_per_day}</dd>
                      </div>
                    ) : null}
                    {obHas(s.annual_inbound_deliveries) ? (
                      <div>
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          Annual inbound deliveries
                        </dt>
                        <dd className="text-[#374151]">{obCompact(s.annual_inbound_deliveries)}</dd>
                      </div>
                    ) : null}
                    {obHas(s.annual_outbound_orders) ? (
                      <div>
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          Annual outbound orders
                        </dt>
                        <dd className="text-[#374151]">{obCompact(s.annual_outbound_orders)}</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                        Labor cost (USD)
                      </dt>
                      <dd className="text-[#374151]">{obLaborLabel(s)}</dd>
                    </div>
                    {obHas(s.inventory_value_usd) ? (
                      <div>
                        <dt className="text-[10px] uppercase tracking-wider text-[#6B7280]">
                          Inventory value (USD)
                        </dt>
                        <dd className="text-[#374151]">{obInvLabel(s)}</dd>
                      </div>
                    ) : null}
                  </dl>
                  {s.notes ? (
                    <p
                      className="mt-2 text-[11px] text-[#6B7280] whitespace-pre-wrap break-words"
                      title={s.notes}
                    >
                      {s.notes}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <WarehouseDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        value={form}
        onChange={setForm}
        onSave={save}
        saving={saving}
        isFinalized={isFinalized}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Finding Dialog — single reusable modal for Create + Edit + Duplicate
// ---------------------------------------------------------------------------
const EMPTY_FINDING = {
  id: null,
  operational_area: "",
  sub_process: "",
  title: "",
  description: "",
  priority: "medium",
  impact: "medium",
  impact_area: "",
  frequency_volume: "",
  applicable_site_ids: [],
  observation_notes: "",
  root_cause_hypothesis: "",
  existing_workaround: "",
  kpi_metric_label: "",
  kpi_unit: "",
  kpi_current_value: null,
  kpi_target_value: null,
  kpi_direction: "increase",
  status: "draft",
};

function Field({ label, required, helper, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-[#374151] mb-1">
        {label} {required ? <span className="text-[#B91C1C]">*</span> : null}
      </label>
      {children}
      {helper ? (
        <p className="text-[10px] text-[#94A3B8] mt-1">{helper}</p>
      ) : null}
    </div>
  );
}

function PriorityChips({ value, onChange, testId, options }) {
  return (
    <div className="grid grid-cols-4 gap-2" data-testid={testId}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            data-state={active ? "selected" : "unselected"}
            className={[
              "px-2 py-1.5 text-[11px] font-medium rounded-sm border transition",
              active
                ? "bg-[#025979] text-white border-[#025979] shadow-sm"
                : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#025979] hover:bg-[#F0F7FA]",
            ].join(" ")}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FindingDialog({
  open,
  onOpenChange,
  value,
  onChange,
  onSave,
  saving,
  sites,
  isFinalized,
  processes,
  subsByProcess,
}) {
  const isEdit = !!value?.id;
  const set = (p) => onChange({ ...value, ...p });
  const subOptions = subsByProcess[value?.operational_area] || [];
  const toggleSite = (sid) => {
    const cur = value?.applicable_site_ids || [];
    set({
      applicable_site_ids: cur.includes(sid)
        ? cur.filter((x) => x !== sid)
        : [...cur, sid],
    });
  };

  const validForSave =
    !!(value?.title || "").trim() &&
    !!(value?.operational_area || "").trim() &&
    (value?.applicable_site_ids || []).length >= 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="ci-finding-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            <span className="flex items-center gap-2">
              {value?.finding_code ? (
                <span
                  className="text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm"
                  data-testid="ci-finding-dialog-code"
                >
                  {value.finding_code}
                </span>
              ) : null}
              <span>
                {isEdit ? "Edit Finding" : "Add Finding"}
              </span>
            </span>
          </DialogTitle>
          <DialogDescription>
            One finding represents one operational concern — issue,
            observation, KPI gap, process inefficiency, or improvement
            opportunity.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-5">
          {/* A. Core Finding */}
          <section data-testid="ci-finding-section-core">
            <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979] mb-2">
              Core Finding
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Business process" required>
                <Select
                  value={value?.operational_area || ""}
                  onValueChange={(v) =>
                    set({ operational_area: v, sub_process: "" })
                  }
                >
                  <SelectTrigger
                    className="rounded-sm text-xs"
                    data-testid="ci-finding-process"
                  >
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {processes.map((p) => (
                      <SelectItem key={p.key} value={p.key}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Sub-process">
                <Select
                  value={value?.sub_process || ""}
                  onValueChange={(v) => set({ sub_process: v })}
                  disabled={!subOptions.length}
                >
                  <SelectTrigger
                    className="rounded-sm text-xs"
                    data-testid="ci-finding-subprocess"
                  >
                    <SelectValue
                      placeholder={
                        subOptions.length ? "Select" : "Select a process first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {subOptions.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Finding title" required>
                  <Input
                    value={value?.title || ""}
                    onChange={(e) => set({ title: e.target.value })}
                    placeholder="e.g. Outbound wave release delays at peak"
                    className="rounded-sm text-xs"
                    data-testid="ci-finding-title"
                  />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Description">
                  <Textarea
                    value={value?.description || ""}
                    onChange={(e) => set({ description: e.target.value })}
                    rows={3}
                    className="rounded-sm text-xs"
                    placeholder="What is happening operationally? Be specific."
                  />
                </Field>
              </div>
            </div>
          </section>

          {/* B. Operational Impact */}
          <section data-testid="ci-finding-section-impact">
            <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979] mb-2">
              Operational Impact
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Priority" required>
                <PriorityChips
                  value={value?.priority || "medium"}
                  onChange={(v) => set({ priority: v })}
                  options={PRIORITY_OPTIONS}
                  testId="ci-finding-priority"
                />
              </Field>
              <Field label="Impact" required>
                <PriorityChips
                  value={value?.impact || "medium"}
                  onChange={(v) => set({ impact: v })}
                  options={IMPACT_OPTIONS}
                  testId="ci-finding-impact"
                />
              </Field>
              <Field
                label="Impact Area"
                helper="Which dimension of operations is most affected."
              >
                <Select
                  value={value?.impact_area || ""}
                  onValueChange={(v) => set({ impact_area: v })}
                >
                  <SelectTrigger
                    className="rounded-sm text-xs"
                    data-testid="ci-finding-impact-area"
                  >
                    <SelectValue placeholder="Select impact area" />
                  </SelectTrigger>
                  <SelectContent>
                    {IMPACT_AREA_OPTIONS.map((i) => (
                      <SelectItem key={i} value={i}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Frequency / Volume">
                <Input
                  value={value?.frequency_volume || ""}
                  onChange={(e) => set({ frequency_volume: e.target.value })}
                  placeholder="e.g. 15 delayed trucks/day, 200 exceptions/week"
                  className="rounded-sm text-xs"
                />
              </Field>
            </div>
          </section>

          {/* C. Warehouses */}
          <section data-testid="ci-finding-section-warehouses">
            <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979] mb-2">
              Applicable Warehouses <span className="text-[#B91C1C]">*</span>
            </h4>
            <div className="flex flex-wrap gap-2">
              {(sites || []).length === 0 ? (
                <p className="text-[11px] text-[#94A3B8] italic">
                  No warehouses captured for this engagement.
                </p>
              ) : (
                (sites || []).map((s) => {
                  const active = (value?.applicable_site_ids || []).includes(
                    s.id
                  );
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleSite(s.id)}
                      className={[
                        "px-3 py-1.5 text-xs rounded-sm border transition",
                        active
                          ? "bg-[#025979] text-white border-[#025979]"
                          : "bg-white text-[#374151] border-[#E5E7EB] hover:border-[#94A3B8]",
                      ].join(" ")}
                    >
                      {s.warehouse_name}
                    </button>
                  );
                })
              )}
            </div>
            {(sites || []).length > 0 && (value?.applicable_site_ids || []).length === 0 ? (
              <p className="text-[10px] text-[#B91C1C] mt-1.5" data-testid="ci-finding-warehouses-error">
                Select at least one warehouse.
              </p>
            ) : null}
          </section>

          {/* D. Operational Analysis */}
          <section data-testid="ci-finding-section-analysis">
            <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979] mb-2">
              Operational Analysis
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Field label="Observation notes">
                  <Textarea
                    value={value?.observation_notes || ""}
                    onChange={(e) =>
                      set({ observation_notes: e.target.value })
                    }
                    rows={2}
                    className="rounded-sm text-xs"
                  />
                </Field>
              </div>
              <Field label="Root cause notes">
                <Textarea
                  value={value?.root_cause_hypothesis || ""}
                  onChange={(e) =>
                    set({ root_cause_hypothesis: e.target.value })
                  }
                  rows={3}
                  className="rounded-sm text-xs"
                />
              </Field>
              <Field label="Existing workaround">
                <Textarea
                  value={value?.existing_workaround || ""}
                  onChange={(e) =>
                    set({ existing_workaround: e.target.value })
                  }
                  rows={3}
                  className="rounded-sm text-xs"
                />
              </Field>
            </div>
          </section>

          {/* E. KPI Impact */}
          <section
            className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3"
            data-testid="ci-finding-section-kpi"
          >
            <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979] mb-2">
              KPI Impact (Optional)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field
                label="KPI metric label"
                helper="Free-text — reuse the same label across findings to allow normalization downstream."
              >
                <Input
                  value={value?.kpi_metric_label || ""}
                  onChange={(e) =>
                    set({ kpi_metric_label: e.target.value })
                  }
                  placeholder="e.g. Dock-to-stock time"
                  className="rounded-sm text-xs"
                />
              </Field>
              <Field label="Unit">
                <Input
                  value={value?.kpi_unit || ""}
                  onChange={(e) => set({ kpi_unit: e.target.value })}
                  placeholder="e.g. minutes, %, ppm"
                  className="rounded-sm text-xs"
                />
              </Field>
              <Field label="Current value">
                <Input
                  type="number"
                  value={value?.kpi_current_value ?? ""}
                  onChange={(e) =>
                    set({
                      kpi_current_value:
                        e.target.value === ""
                          ? null
                          : Number(e.target.value),
                    })
                  }
                  className="rounded-sm text-xs"
                />
              </Field>
              <Field label="Target value">
                <Input
                  type="number"
                  value={value?.kpi_target_value ?? ""}
                  onChange={(e) =>
                    set({
                      kpi_target_value:
                        e.target.value === ""
                          ? null
                          : Number(e.target.value),
                    })
                  }
                  className="rounded-sm text-xs"
                />
              </Field>
              <Field label="Direction">
                <Select
                  value={value?.kpi_direction || "increase"}
                  onValueChange={(v) => set({ kpi_direction: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="increase">
                      Increase is better
                    </SelectItem>
                    <SelectItem value="decrease">
                      Decrease is better
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          {/* F. Internal Metadata */}
          <section data-testid="ci-finding-section-meta">
            <h4 className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[#025979] mb-2">
              Internal Metadata
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Status">
                <Select
                  value={value?.status || "draft"}
                  onValueChange={(v) => set({ status: v })}
                >
                  <SelectTrigger
                    className="rounded-sm text-xs"
                    data-testid="ci-finding-status"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {value?.updated_at ? (
                <Field label="Last updated">
                  <p className="text-xs text-[#374151] py-1.5">
                    {value.updated_at?.slice(0, 16).replace("T", " ")}
                  </p>
                </Field>
              ) : null}
              {value?.created_at ? (
                <Field label="Created">
                  <p className="text-xs text-[#374151] py-1.5">
                    {value.created_at?.slice(0, 16).replace("T", " ")}
                  </p>
                </Field>
              ) : null}
            </div>
          </section>
        </div>

        <DialogFooter className="pt-3 border-t border-[#E5E7EB]">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !validForSave || isFinalized}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="ci-finding-save"
          >
            {saving
              ? "Saving…"
              : isEdit
              ? "Save Changes"
              : "Add Finding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Operational Findings table + filter/search/sort bar
// ---------------------------------------------------------------------------
export function OperationalFindingsSection({
  engagementId,
  findings,
  sites,
  onChange,
  isFinalized,
}) {
  // Admin-managed Business Process + Sub-process catalogue — drives the
  // Edit Finding modal dropdowns, the filter dropdown, and the table
  // label resolver. Any Business Process added in Platform → Reference
  // Models shows up here automatically on the next page load.
  const { processes, subsByProcess, labelByKey } = useProcessCatalog();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [filterProcess, setFilterProcess] = useState("__all__");
  const [filterWarehouse, setFilterWarehouse] = useState("__all__");
  const [filterPriority, setFilterPriority] = useState("__all__");
  const [filterStatus, setFilterStatus] = useState("__all__");
  const [sortKey, setSortKey] = useState("finding_id");
  const [showFilters, setShowFilters] = useState(false);

  const siteName = (sid) =>
    (sites || []).find((s) => s.id === sid)?.warehouse_name || "—";

  const filtered = useMemo(() => {
    let rows = [...(findings || [])];
    const s = search.trim().toLowerCase();
    if (s) {
      rows = rows.filter(
        (f) =>
          (f.title || "").toLowerCase().includes(s) ||
          (f.description || "").toLowerCase().includes(s) ||
          (f.sub_process || "").toLowerCase().includes(s)
      );
    }
    if (filterProcess !== "__all__") {
      rows = rows.filter((f) => f.operational_area === filterProcess);
    }
    if (filterWarehouse !== "__all__") {
      rows = rows.filter((f) =>
        (f.applicable_site_ids || []).includes(filterWarehouse)
      );
    }
    if (filterPriority !== "__all__") {
      rows = rows.filter(
        (f) => (f.priority || f.severity) === filterPriority
      );
    }
    if (filterStatus !== "__all__") {
      rows = rows.filter(
        (f) => (f.status || "draft") === filterStatus
      );
    }
    const priRank = { critical: 0, high: 1, medium: 2, low: 3 };
    // Stable ascending sort: group by operational-area prefix first, then
    // numeric suffix within each prefix. Restores the enterprise grouped
    // ordering (INB01..INB03, OUT01..OUT03, …) on the Operational Findings
    // table. Used as both the default and the tiebreaker for priority/impact.
    rows.sort((a, b) => {
      if (sortKey === "priority") {
        return (
          (priRank[a.priority || a.severity] ?? 4) -
          (priRank[b.priority || b.severity] ?? 4) ||
          compareFindings(a, b)
        );
      }
      if (sortKey === "impact") {
        return (
          (priRank[a.impact] ?? 4) - (priRank[b.impact] ?? 4) ||
          compareFindings(a, b)
        );
      }
      if (sortKey === "updated_at_desc") {
        return (b.updated_at || "").localeCompare(a.updated_at || "");
      }
      // "finding_id" — default Feb 2026: workshop traceability order.
      return compareFindings(a, b);
    });
    return rows;
  }, [findings, search, filterProcess, filterWarehouse, filterPriority, filterStatus, sortKey]);

  const openCreate = () => {
    setForm({ ...EMPTY_FINDING });
    setDialogOpen(true);
  };

  const openEdit = (f) => {
    setForm({
      ...EMPTY_FINDING,
      ...f,
      // Back-compat: ensure all fields are present on edit
      priority: f.priority || f.severity || "medium",
      // Existing legacy issues don't have an "impact" enum — derive a
      // sensible default from priority so the chip renders.
      impact:
        f.impact && ["low", "medium", "high", "critical"].includes(f.impact)
          ? f.impact
          : f.priority || f.severity || "medium",
      status: normalizeStatus(f.status),
      applicable_site_ids: f.applicable_site_ids || [],
      kpi_current_value: f.kpi_current_value ?? null,
      kpi_target_value: f.kpi_target_value ?? null,
    });
    setDialogOpen(true);
  };

  const openDuplicate = (f) => {
    setForm({
      ...EMPTY_FINDING,
      ...f,
      id: null, // force create-new path
      finding_code: "", // a fresh code will be allocated on save
      title: `${f.title} (copy)`,
      status: "draft",
      created_at: undefined,
      updated_at: undefined,
      priority: f.priority || f.severity || "medium",
      impact:
        f.impact && ["low", "medium", "high", "critical"].includes(f.impact)
          ? f.impact
          : f.priority || f.severity || "medium",
      applicable_site_ids: f.applicable_site_ids || [],
    });
    setDialogOpen(true);
  };

  const changeStatus = async (f, newStatus) => {
    if (isFinalized) {
      toast.error("Engagement is finalized — status changes disabled.");
      return;
    }
    const current = normalizeStatus(f.status);
    if (newStatus === current) return;
    try {
      await updateIssue(f.id, { status: newStatus });
      toast.success(`Status → ${STATUS_LABEL[newStatus] || newStatus}`);
      await onChange();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const saveFinding = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        operational_area: form.operational_area,
        sub_process: form.sub_process || "",
        title: form.title,
        description: form.description || "",
        priority: form.priority || "medium",
        // Mirror priority -> severity so legacy engines stay happy.
        severity: form.priority || "medium",
        impact:
          form.impact && ["low", "medium", "high", "critical"].includes(form.impact)
            ? form.impact
            : "medium",
        impact_area: form.impact_area || "",
        frequency_volume: form.frequency_volume || "",
        observation_notes: form.observation_notes || "",
        root_cause_hypothesis: form.root_cause_hypothesis || "",
        existing_workaround: form.existing_workaround || "",
        applicable_site_ids: form.applicable_site_ids || [],
        kpi_metric_label: form.kpi_metric_label || "",
        kpi_unit: form.kpi_unit || "",
        kpi_current_value: form.kpi_current_value,
        kpi_target_value: form.kpi_target_value,
        kpi_direction: form.kpi_direction || "increase",
        status: form.status || "draft",
      };
      if (form.id) {
        await updateIssue(form.id, payload);
        toast.success("Finding updated");
      } else {
        await addIssue(engagementId, payload);
        toast.success("Finding added");
      }
      setDialogOpen(false);
      await onChange();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to save finding");
    } finally {
      setSaving(false);
    }
  };

  const activeFilterCount =
    (filterProcess !== "__all__" ? 1 : 0) +
    (filterWarehouse !== "__all__" ? 1 : 0) +
    (filterPriority !== "__all__" ? 1 : 0) +
    (filterStatus !== "__all__" ? 1 : 0);

  const clearFilters = () => {
    setFilterProcess("__all__");
    setFilterWarehouse("__all__");
    setFilterPriority("__all__");
    setFilterStatus("__all__");
  };

  return (
    <section
      className="bg-white border border-[#E5E7EB] rounded-sm"
      data-testid="ci-operational-findings-section"
    >
      <header className="px-5 py-3 border-b border-[#E5E7EB]">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-[#111827]">
              Findings
            </h2>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F1F5F9] text-[#475569]"
              data-testid="ci-findings-count"
            >
              {(findings || []).length}
            </span>
            {filtered.length !== (findings || []).length ? (
              <span className="text-[10px] text-[#6B7280]">
                · {filtered.length} shown
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#94A3B8] absolute left-2 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search findings…"
                className="h-8 pl-7 pr-2 rounded-sm text-xs w-52"
                data-testid="ci-findings-search"
              />
            </div>
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger
                className="h-8 rounded-sm text-xs w-44"
                data-testid="ci-findings-sort"
              >
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="finding_id">Finding ID</SelectItem>
                <SelectItem value="updated_at_desc">Last Updated</SelectItem>
                <SelectItem value="priority">Priority</SelectItem>
                <SelectItem value="impact">Impact</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => setShowFilters((s) => !s)}
              className="h-8 rounded-sm text-xs border-[#E5E7EB] text-[#374151] hover:border-[#025979] hover:text-[#025979]"
              data-testid="ci-findings-filter-toggle"
            >
              <FilterIcon className="w-3.5 h-3.5 mr-1" />
              Filter
              {activeFilterCount > 0 ? (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#025979] text-white text-[9px]">
                  {activeFilterCount}
                </span>
              ) : null}
            </Button>
            <Button
              onClick={openCreate}
              disabled={isFinalized}
              className="h-8 bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
              data-testid="ci-finding-add"
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Add Finding
            </Button>
          </div>
        </div>
        {showFilters ? (
          <div
            className="mt-3 pt-3 border-t border-[#F1F5F9] grid grid-cols-2 md:grid-cols-4 gap-3"
            data-testid="ci-findings-filters"
          >
            <Select value={filterProcess} onValueChange={setFilterProcess}>
              <SelectTrigger className="h-8 rounded-sm text-xs">
                <SelectValue placeholder="Process" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All processes</SelectItem>
                {processes.map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filterWarehouse}
              onValueChange={setFilterWarehouse}
            >
              <SelectTrigger className="h-8 rounded-sm text-xs">
                <SelectValue placeholder="Warehouse" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All warehouses</SelectItem>
                {(sites || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.warehouse_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="h-8 rounded-sm text-xs">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All priorities</SelectItem>
                {PRIORITY_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 rounded-sm text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeFilterCount > 0 ? (
              <button
                type="button"
                onClick={clearFilters}
                className="md:col-span-4 self-end inline-flex items-center gap-1 text-[10px] text-[#025979] hover:text-[#03738C] w-fit"
              >
                <X className="w-3 h-3" /> Clear filters
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      <div className="overflow-x-auto">
        {(findings || []).length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-[#6B7280]">
            No findings yet. Click{" "}
            <strong>Add Finding</strong> to capture the first.
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-10 text-center text-xs text-[#6B7280]">
            No findings match the current filters.
          </div>
        ) : (
          <table
            className="w-full text-sm"
            data-testid="ci-findings-table"
          >
            <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-3 py-2 font-semibold">Finding ID</th>
                <th className="px-3 py-2 font-semibold">Business Process</th>
                <th className="px-3 py-2 font-semibold">Sub-Process</th>
                <th className="px-3 py-2 font-semibold min-w-[260px]">
                  Finding Title
                </th>
                <th className="px-3 py-2 font-semibold">Priority</th>
                <th className="px-3 py-2 font-semibold">Impact</th>
                <th className="px-3 py-2 font-semibold">Warehouses</th>
                <th className="px-3 py-2 font-semibold">KPI Impact</th>
                <th className="px-3 py-2 font-semibold">Frequency / Volume</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Last Updated</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => {
                const priority = f.priority || f.severity || "medium";
                const impact = ["low", "medium", "high", "critical"].includes(
                  f.impact
                )
                  ? f.impact
                  : priority;
                const wh = (f.applicable_site_ids || []).map(siteName);
                const stat = normalizeStatus(f.status);
                return (
                  <Fragment key={f.id}>
                    <tr
                      className="border-t border-[#F1F5F9] hover:bg-[#FAFBFC]"
                      data-testid={`ci-finding-row-${f.id}`}
                    >
                      <td className="px-3 py-2">
                        <span
                          className="inline-flex text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm whitespace-nowrap"
                          data-testid={`ci-finding-code-${f.id}`}
                          title="Stable workshop finding ID"
                        >
                          {f.finding_code || "—"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-[#374151]">
                        {labelByKey[f.operational_area] ||
                          (f.operational_area || "").replace(/_/g, " ")}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#6B7280]">
                        {f.sub_process || "—"}
                      </td>
                      <td className="px-3 py-2 text-[13px] text-[#111827] font-medium">
                        <button
                          type="button"
                          onClick={() => openEdit(f)}
                          className="text-left hover:text-[#025979] hover:underline"
                          data-testid={`ci-finding-title-${f.id}`}
                        >
                          {f.title}
                        </button>
                        {f.impact_area ? (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm bg-[#EEF2FF] text-[#3730A3] border border-[#C7D2FE]">
                            {f.impact_area}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${
                            PRIORITY_CHIP[priority] || ""
                          }`}
                        >
                          {priority}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm capitalize ${
                            PRIORITY_CHIP[impact] || ""
                          }`}
                        >
                          {impact}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-[#374151]">
                        {wh.length === 0 ? (
                          <span className="text-[#94A3B8]">—</span>
                        ) : wh.length <= 2 ? (
                          wh.join(", ")
                        ) : (
                          <span>
                            {wh.slice(0, 2).join(", ")}{" "}
                            <span className="text-[#94A3B8]">
                              +{wh.length - 2}
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#374151]">
                        {f.kpi_metric_label ? (
                          <div className="leading-tight">
                            <div className="font-medium">{f.kpi_metric_label}</div>
                            <div className="text-[10px] text-[#6B7280]">
                              {f.kpi_current_value ?? "—"} → {f.kpi_target_value ?? "—"}{" "}
                              {f.kpi_unit || ""}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[#94A3B8]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#6B7280]">
                        {f.frequency_volume || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={stat}
                          onValueChange={(v) => changeStatus(f, v)}
                          disabled={isFinalized}
                        >
                          <SelectTrigger
                            className={`h-7 px-2 rounded-sm text-[10px] font-semibold border w-[150px] ${
                              STATUS_CHIP[stat] || ""
                            }`}
                            data-testid={`ci-finding-status-trigger-${f.id}`}
                          >
                            <SelectValue>{displayStatus(f.status)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem
                                key={s.value}
                                value={s.value}
                                data-testid={`ci-finding-status-option-${f.id}-${s.value}`}
                              >
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-[#6B7280]">
                        {(f.updated_at || "").slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(f)}
                            className="p-1.5 text-[#025979] hover:bg-[#F0F7FA] rounded-sm"
                            title="View / Edit"
                            data-testid={`ci-finding-edit-${f.id}`}
                            disabled={isFinalized}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => openDuplicate(f)}
                            className="p-1.5 text-[#025979] hover:bg-[#F0F7FA] rounded-sm"
                            title="Duplicate"
                            data-testid={`ci-finding-duplicate-${f.id}`}
                            disabled={isFinalized}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <FindingDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        value={form}
        onChange={setForm}
        onSave={saveFinding}
        saving={saving}
        sites={sites}
        isFinalized={isFinalized}
        processes={processes}
        subsByProcess={subsByProcess}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Top-level orchestrator — replaces the legacy ClientInputsTab body.
// ---------------------------------------------------------------------------
export default function ClientInputsWorkspace({
  engagement,
  sites,
  findings,
  onChange,
}) {
  return (
    <div
      className="space-y-5"
      data-testid="smartimprove-client-inputs-tab"
    >
      <EngagementOverviewExpanded
        engagement={engagement}
        sites={sites}
        findings={findings}
      />
      <WarehousesSitesPanel
        engagementId={engagement.id}
        sites={sites}
        findings={findings}
        onChange={onChange}
        isFinalized={!!engagement?.is_finalized}
      />
      <OperationalFindingsSection
        engagementId={engagement.id}
        findings={findings}
        sites={sites}
        onChange={onChange}
        isFinalized={!!engagement?.is_finalized}
      />
    </div>
  );
}

// Re-exported for tests
export const __TEST__ = {
  BUSINESS_PROCESSES,
  PRIORITY_OPTIONS,
  STATUS_OPTIONS,
  EMPTY_FINDING,
};
